import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Database, Download, Search, HardDrive } from "lucide-react";
import AxiosDB from "@/Config/AxiosDB";

const formatBytes = (bytes) => {
  const b = Number(bytes) || 0;
  if (b === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return `${parseFloat((b / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

export default function Backups({ user, onLogout }) {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    loadBackups();
  }, []);

  const loadBackups = async () => {
    try {
      const res = await AxiosDB.get(`${API}/backups`);

      setBackups(res.data);
    } catch (error) {
      toast.error("Error al cargar los backups");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (backup) => {
    setDownloadingId(backup.id);
    try {
      // const res = await axios.get(`${API}/backups/${backup.id}/download`, { responseType: "blob" });
      const res = await AxiosDB.get(`${API}/backups/${encodeURIComponent(eid)}/download`);

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", backup.nombre);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Descarga iniciada");
    } catch (error) {
      toast.error("Error al descargar el backup");
    } finally {
      setDownloadingId(null);
    }
  };

  const filtered = backups.filter((b) => b.nombre?.toLowerCase().includes(search.toLowerCase()));
  const totalTamano = backups.reduce((a, b) => a + (parseInt(b.tamano) || 0), 0);

  return (
    <Layout user={user} onLogout={onLogout} title="Backup">
      <Card data-testid="backups-card">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-600 rounded-lg">
                <Database className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle>Respaldos de Base de Datos</CardTitle>
                <CardDescription>Lista de backups disponibles para tu empresa</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2">
              <HardDrive className="w-4 h-4 text-slate-500" />
              <span><strong>{backups.length}</strong> backups · <strong>{formatBytes(totalTamano)}</strong> en total</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Buscar por nombre de archivo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-backup-input"
            />
          </div>

          {loading ? (
            <div className="text-center py-10 text-slate-500 text-sm">Cargando...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm" data-testid="backups-empty">
              {backups.length === 0 ? "No hay backups disponibles." : "No se encontraron resultados."}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Nombre del Archivo</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Empresa</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Fecha</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Tamaño</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b) => (
                    <tr key={b.id} className="border-b border-slate-100 last:border-0 hover:bg-blue-50/40 transition-colors" data-testid={`backup-row-${b.id}`}>
                      <td className="py-2 px-3 font-mono text-xs text-slate-800 flex items-center gap-2">
                        <Database className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        {b.nombre}
                      </td>
                      <td className="py-2 px-3 text-slate-600">{b.empresa}</td>
                      <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{b.fecha || "-"}</td>
                      <td className="py-2 px-3 text-right font-semibold text-slate-800 whitespace-nowrap">{formatBytes(b.tamano)}</td>
                      {/* <td className="py-2 px-3 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownload(b)}
                          disabled={downloadingId === b.id}
                          data-testid={`download-backup-${b.id}`}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          {downloadingId === b.id ? "Descargando..." : "Descargar"}
                        </Button>
                      </td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
}
